import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Notification } from '@weirgroup/weir-dls';
import { useDarkMode } from '../hooks/use-darkmode';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import './global-error.styles.scss';

interface ErrorNotification {
    id: string;
    title: string;
    message: string;
    variant: 'success' | 'critical' | 'warning' | 'info';
    duration?: number;
}

interface GlobalErrorContextType {
    showError: (message: string, title?: string, variant?: 'critical' | 'warning' | 'info') => void;
    showSuccess: (message: string, title?: string) => void;
    showInfo: (message: string, title?: string) => void;
    showWarning: (message: string, title?: string) => void;
    clearNotification: (id: string) => void;
    clearAllNotifications: () => void;
}

const GlobalErrorContext = createContext<GlobalErrorContextType | undefined>(undefined);

interface GlobalErrorProviderProps {
    children: ReactNode;
}

export const GlobalErrorProvider: React.FC<GlobalErrorProviderProps> = ({ children }) => {
    const [notifications, setNotifications] = useState<ErrorNotification[]>([]);
    const { darkMode } = useDarkMode();
    const { t } = useTranslation();

    const generateId = (): string => {
        return `notification-${uuidv4()}`;
    };

    const addNotification = (
        message: string, 
        title: string, 
        variant: 'success' | 'critical' | 'warning' | 'info',
        duration: number = 5000
    ) => {
        const id = generateId();
        const notification: ErrorNotification = {
            id,
            title,
            message,
            variant,
            duration
        };

        setNotifications(prev => [...prev, notification]);

        // Auto-remove notification after duration
        if (duration > 0) {
            setTimeout(() => {
                clearNotification(id);
            }, duration);
        }
    };

    const showError = (message: string, title?: string, variant: 'critical' | 'warning' | 'info' = 'critical') => {
        addNotification(
            message, 
            title || t('errorTitle', 'Error'), 
            variant
        );
    };

    const showSuccess = (message: string, title?: string) => {
        addNotification(
            message, 
            title || t('successTitle', 'Success'), 
            'success'
        );
    };

    const showInfo = (message: string, title?: string) => {
        addNotification(
            message, 
            title || t('infoTitle', 'Information'), 
            'info'
        );
    };

    const showWarning = (message: string, title?: string) => {
        addNotification(
            message, 
            title || t('warningTitle', 'Warning'), 
            'warning'
        );
    };

    const clearNotification = (id: string) => {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
    };

    const clearAllNotifications = () => {
        setNotifications([]);
    };

    const contextValue: GlobalErrorContextType = {
        showError,
        showSuccess,
        showInfo,
        showWarning,
        clearNotification,
        clearAllNotifications
    };

    return (
        <GlobalErrorContext.Provider value={contextValue}>
            {children}
            
            {/* Global Notification Container */}
            <div className="global-notifications-container">
                {notifications.map((notification) => (
                    <div key={notification.id} className="global-notification-item">
                        <Notification
                            darkMode={darkMode}
                            title={notification.title}
                            textContent={notification.message}
                            variant={notification.variant}
                            onClose={() => clearNotification(notification.id)}
                        />
                    </div>
                ))}
            </div>
        </GlobalErrorContext.Provider>
    );
};

export const useGlobalError = (): GlobalErrorContextType => {
    const context = useContext(GlobalErrorContext);
    if (context === undefined) {
        throw new Error('useGlobalError must be used within a GlobalErrorProvider');
    }
    return context;
};
